import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowRight,
  Activity,
  Crosshair,
  Droplets,
  Layers,
  Clock,
  ShieldCheck,
  CheckCircle2,
  XCircle,
  ChevronRight,
  ChevronDown,
  BookOpen,
  Target as TargetIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Layout } from "@/components/site/Layout";
import { ArticleCard } from "@/components/site/ArticleCard";
import { getPublicArticles } from "@/lib/articles";
import smcHero from "@/assets/smc-pillar-hero.webp";

const SMC_HERO_OG = "https://xauproacademy.com/smc-pillar-hero.jpg";
const PAGE_URL = "https://xauproacademy.com/smart-money-concepts";

const FAQ = [
  {
    q: "What are Smart Money Concepts (SMC)?",
    a: "Smart Money Concepts is a price action methodology that models how institutional participants (banks, hedge funds, prop desks) accumulate and distribute positions through liquidity sweeps, market structure shifts, and order block reactions. It replaces lagging indicators with structural reads of the chart.",
  },
  {
    q: "Is SMC profitable on gold (XAUUSD)?",
    a: "XAUUSD is one of the cleanest instruments for SMC because of its deep liquidity and well-defined session behavior during London and New York. The methodology itself is sound. Profitability depends on disciplined execution, strict risk control, and filtering out low-probability setups during macro-driven volatility.",
  },
  {
    q: "What is the difference between SMC and ICT?",
    a: "ICT (Inner Circle Trader) is the original, encyclopedic methodology developed by Michael Huddleston. SMC is a streamlined distillation that keeps the load-bearing pillars (liquidity, structure, order blocks, FVGs) and drops most of the complex pattern naming. SMC is the better starting point. ICT is a useful reference library once you are executing cleanly.",
  },
  {
    q: "How long does it take to learn SMC?",
    a: "Expect three to six months of focused study and journaled trades to build pattern recognition. The concepts are simple. The execution discipline (passing on B-grade setups, waiting for inducement sweeps, sitting through killzones) is what takes time.",
  },
  {
    q: "What is the best timeframe for SMC trading?",
    a: "For XAUUSD we use the H4 chart to define directional bias and the M15 chart to time entries after a liquidity sweep. Lower timeframes work but increase noise and emotional decisions. Higher timeframes work for swing traders.",
  },
  {
    q: "Do I need an indicator to trade SMC?",
    a: "No. SMC is purely structural. The only tools required are a clean chart and the ability to mark swing highs, swing lows, equal highs, equal lows, order blocks, and fair value gaps. Indicators add noise and conflict with the institutional read.",
  },
];

export const Route = createFileRoute("/smart-money-concepts")({
  head: () => ({
    meta: [
      { title: "Smart Money Concepts for Gold Trading | XAU Pro Academy" },
      {
        name: "description",
        content:
          "The institutional SMC framework applied to XAUUSD. Master liquidity, market structure, order blocks, and FVGs with the only SMC academy for gold.",
      },
      {
        name: "keywords",
        content:
          "smart money concepts, smc, smc trading, smc trader, ict concepts, order blocks, fair value gap, liquidity sweep, smart money reversal, smc gold, xauusd smc",
      },
      { property: "og:title", content: "Smart Money Concepts (SMC) for Gold Trading" },
      {
        property: "og:description",
        content:
          "The institutional SMC framework applied to XAUUSD. Liquidity, structure, order blocks, FVGs, and a Macro Defense Layer.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: PAGE_URL },
      { property: "og:image", content: SMC_HERO_OG },
      { property: "og:image:width", content: "1920" },
      { property: "og:image:height", content: "1080" },
      { property: "og:image:alt", content: "Abstract gold candlestick chart over deep navy background, illustrating Smart Money Concepts liquidity zones" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: SMC_HERO_OG },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "Smart Money Concepts (SMC) for Gold Trading",
          url: PAGE_URL,
          image: SMC_HERO_OG,
          description:
            "The institutional Smart Money Concepts framework applied to XAUUSD. Liquidity, market structure, order blocks, FVGs, and the ORION Macro Defense Layer.",
          isPartOf: {
            "@type": "WebSite",
            name: "XAU Pro Academy",
            url: "https://xauproacademy.com",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://xauproacademy.com/" },
            { "@type": "ListItem", position: 2, name: "Smart Money Concepts", item: PAGE_URL },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "DefinedTermSet",
          name: "Smart Money Concepts Glossary for XAUUSD",
          url: PAGE_URL,
          hasDefinedTerm: [
            ["Break of Structure (BOS)", "A candle body close beyond a prior swing point with displacement, confirming trend continuation."],
            ["Change of Character (CHoCH)", "The first break against the prevailing structure, an early reversal warning that requires displacement confirmation."],
            ["Order Block", "The last opposing candle before a displacement move that swept liquidity and broke structure."],
            ["Fair Value Gap (FVG)", "A three candle imbalance where price moved too fast for both sides to trade, often revisited for rebalance."],
            ["Liquidity Sweep", "A move beyond equal highs, equal lows or session extremes that triggers resting stop orders before reversal or continuation."],
            ["Inducement (IDM)", "A minor liquidity pool that lures early entries before price reaches the true point of interest."],
          ].map(([name, description]) => ({ "@type": "DefinedTerm", name, description })),
        }),
      },
    ],
  }),
  component: SmcPillarPage,
});

function SmcPillarPage() {
  const smcArticles = getPublicArticles()
    .filter((a) =>
      /smc|liquidity|inducement|order block|fvg|smart money/i.test(
        `${a.category} ${a.title} ${a.focusKeyword} ${a.secondaryKeywords.join(" ")}`,
      ),
    )
    .slice(0, 6);

  return (
    <Layout>
      {/* 1. Hero */}
      <section className="relative overflow-hidden bg-gradient-hero">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <img
          src={smcHero}
          alt="Smart Money Concepts on a XAUUSD chart: liquidity, market structure and order blocks"
          width={1920}
          height={1080}
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover opacity-25 mix-blend-luminosity"
        />
        <div
          className="pointer-events-none absolute -top-40 left-1/2 h-[32rem] w-[56rem] -translate-x-1/2 rounded-full opacity-40 blur-3xl"
          style={{ background: "radial-gradient(closest-side, hsl(var(--gold) / 0.45), transparent)" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/55 to-background" />

        <div className="container relative mx-auto max-w-7xl px-4 pb-24 pt-20 md:pt-32">
          <div className="mx-auto max-w-3xl text-center">
            <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold-muted px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-gold">
              <Crosshair className="h-3 w-3" /> The SMC Pillar
            </div>
            <h1 className="mt-6 font-display text-4xl font-bold leading-[1.05] tracking-tight md:text-6xl lg:text-7xl">
              Smart Money Concepts:{" "}
              <span className="text-gradient-gold">The Institutional Framework for Trading Gold</span>.
            </h1>
            <p className="mt-6 font-display text-xl text-gradient-gold md:text-2xl">
              Liquidity. Structure. Order flow.
            </p>
            <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">
              The only Smart Money Concepts academy built specifically for XAUUSD.
              Read the chart the way institutions do, then let the ORION engine
              veto setups when macro conditions turn statistically unsafe.
            </p>
            <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Button asChild variant="gold" size="xl">
                <Link to="/signals">
                  View the Signals Desk <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="xl">
                <Link to="/education">Start Learning Free</Link>
              </Button>
            </div>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              <span>Liquidity mapping</span>
              <span className="text-border">·</span>
              <span>Structural reads</span>
              <span className="text-border">·</span>
              <span>Macro defense layer</span>
            </div>
          </div>
        </div>
      </section>

      {/* 2. What SMC actually is */}
      <section className="container mx-auto max-w-3xl px-4 py-12 md:py-24">
        <div className="space-y-6 text-base leading-relaxed text-muted-foreground md:text-lg">
          <p>
            <span className="font-semibold text-foreground">Smart Money Concepts (SMC)</span>{" "}
            is the modern, structural lens for reading institutional order flow on a
            chart. It assumes the market is not random and not driven by indicators.
            It is engineered around how large participants accumulate and distribute
            positions through liquidity.
          </p>
          <p>
            Where retail analysis sees support and resistance, SMC sees{" "}
            <span className="font-semibold text-foreground">resting liquidity</span>.
            Where retail sees a "false breakout," SMC sees an institutional fill.
            Where retail sees a clean trendline, SMC sees the inducement that traps
            early entrants before the real move.
          </p>
          <blockquote className="rounded-xl border-l-2 border-gold bg-slate-deep/60 py-4 pl-6 pr-4 font-display text-lg italic text-foreground md:text-xl">
            The chart is not a price history. It is a record of where liquidity was
            harvested and where institutional orders were placed.
          </blockquote>
        </div>
      </section>

      {/* 3. Four pillars */}
      <section className="border-y border-border/60 bg-slate-deep">
        <div className="container mx-auto max-w-6xl px-4 py-20 md:py-24">
          <div className="mx-auto max-w-2xl text-center">
            <div className="font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-gold">
              Foundations
            </div>
            <h2 className="mt-2 font-display text-3xl font-bold tracking-tight md:text-4xl">
              The Four Pillars of Smart Money Concepts
            </h2>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground md:text-lg">
              Every SMC analysis stands on four pillars. Master these and the rest
              of the framework falls into place.
            </p>
          </div>

          <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            <PillarCard
              num={1}
              icon={<Activity className="h-5 w-5" />}
              title="Market Structure"
              body="BOS and CHoCH on body close logic. The skeleton of every SMC read."
              href="/education/inducement-trap-idm-smc"
            />
            <PillarCard
              num={2}
              icon={<Droplets className="h-5 w-5" />}
              title="Liquidity"
              body="BSL, SSL, equal highs, equal lows, and the inducement (IDM) before every valid POI."
              href="/education/liquidity-purge-eqh-eql-gold"
            />
            <PillarCard
              num={3}
              icon={<Layers className="h-5 w-5" />}
              title="Order Blocks & FVG"
              body="The institutional points of interest where price reacts on the return."
              href="/education/order-blocks-fvg-explained"
            />
            <PillarCard
              num={4}
              icon={<Clock className="h-5 w-5" />}
              title="Killzones & Sessions"
              body="London and New York Judas swings. Time of day is structural, not optional."
              href="/education/amd-pattern-gold-daily-cycles"
            />
          </div>
        </div>
      </section>

      {/* 3b. Why retail SMC fails on gold without macro defense */}
      <section className="container mx-auto max-w-6xl px-4 py-20 md:py-24">
        <div className="mx-auto max-w-2xl text-center">
          <div className="font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-gold">
            The Missing Layer
          </div>
          <h2 className="mt-2 font-display text-3xl font-bold tracking-tight md:text-4xl">
            Why Retail SMC Fails on Gold Without Macro Defense
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground md:text-lg">
            Most retail traders apply SMC in a vacuum. On XAUUSD, that is the
            single most expensive mistake.
          </p>
        </div>

        <div className="mx-auto mt-10 max-w-3xl space-y-6 text-base leading-relaxed text-muted-foreground md:text-lg">
          <p>
            The standard retail SMC loop looks correct on paper: mark liquidity,
            wait for the sweep, confirm displacement, enter from the order block.
            What the loop ignores is that gold is the most macro-sensitive
            instrument a retail trader can pick. A technically flawless bullish
            order block does not survive a hot CPI print or a hawkish FOMC
            statement. The liquidity that swept was not always institutional
            positioning. Sometimes it was the market repricing a rate
            expectation in one candle.
          </p>
          <p>
            The result is predictable: textbook setups with clean stops still
            fail, not because the structure was wrong, but because the
            fundamental regime had shifted beneath it. Structure tells you
            where price is likely to react. It cannot tell you what the
            largest holders in the market were doing that week, or whether
            inflation data just changed the odds on the next rate decision.
          </p>
          <blockquote className="rounded-xl border-l-2 border-gold bg-slate-deep/60 py-4 pl-6 pr-4 font-display text-lg italic text-foreground md:text-xl">
            Structure provides the entry zone. Macro context provides the
            defense. On XAUUSD, one without the other is incomplete.
          </blockquote>
          <p>
            A complete gold framework requires pairing SMC market structure
            with verified institutional positioning (CFTC COT data), inflation
            cycles, and session liquidity dynamics. When the structural read
            and the macro read agree, the setup earns a place in the execution
            queue. When they conflict, the discipline is to stand aside.
          </p>
        </div>

        <div className="mt-12 grid gap-4 md:grid-cols-3">
          <MacroLink
            href="/gold-cot-report"
            label="Institutional positioning"
            title="Gold COT Report"
            body="What funds, banks and producers did in COMEX gold this week, straight from official CFTC data."
          />
          <MacroLink
            href="/gold-inflation-cpi"
            label="Inflation regime"
            title="US CPI and Gold"
            body="Official US inflation readings next to gold's monthly move, with release context."
          />
          <MacroLink
            href="/gold-seasonality"
            label="Session tendency"
            title="Gold Seasonality"
            body="How XAUUSD performed in each calendar month over the last 10 years."
          />
        </div>
      </section>

      {/* 4. SMC vs Retail */}
      <section className="container mx-auto max-w-6xl px-4 py-20 md:py-24">
        <div className="mx-auto max-w-2xl text-center">
          <div className="font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-gold">
            The Contrast
          </div>
          <h2 className="mt-2 font-display text-3xl font-bold tracking-tight md:text-4xl">
            SMC vs Retail Analysis
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground md:text-lg">
            The same chart, two different worlds. One reads price. The other reads
            why price moved.
          </p>
        </div>

        <div className="mt-10 grid gap-5 md:grid-cols-2">
          <div className="rounded-2xl border border-border/60 bg-card p-6">
            <div className="flex items-center gap-2 font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-muted-foreground">
              <XCircle className="h-3 w-3" /> Retail Analysis
            </div>
            <h3 className="mt-3 font-display text-xl font-semibold">What most traders see</h3>
            <ul className="mt-5 space-y-3 text-sm leading-relaxed text-muted-foreground">
              <ContrastItem positive={false}>Trendlines and channels drawn after the fact</ContrastItem>
              <ContrastItem positive={false}>RSI, MACD, moving averages chasing price</ContrastItem>
              <ContrastItem positive={false}>Horizontal support and resistance treated as walls</ContrastItem>
              <ContrastItem positive={false}>"Breakout" entries that get reversed on the next candle</ContrastItem>
              <ContrastItem positive={false}>No model for why a level holds or fails</ContrastItem>
            </ul>
          </div>
          <div className="rounded-2xl border border-gold/40 bg-gold-muted/20 p-6">
            <div className="flex items-center gap-2 font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-gold">
              <CheckCircle2 className="h-3 w-3" /> Smart Money Concepts
            </div>
            <h3 className="mt-3 font-display text-xl font-semibold">What institutions see</h3>
            <ul className="mt-5 space-y-3 text-sm leading-relaxed text-foreground/85">
              <ContrastItem positive>Liquidity pools above equal highs and below equal lows</ContrastItem>
              <ContrastItem positive>Market structure read on candle body closes only</ContrastItem>
              <ContrastItem positive>Order blocks with FVG displacement as entry zones</ContrastItem>
              <ContrastItem positive>Sweeps treated as the entry signal, not the trap</ContrastItem>
              <ContrastItem positive>Inducement filter that rejects low-probability setups</ContrastItem>
            </ul>
          </div>
        </div>
      </section>

      {/* 5. How XAU Pro applies SMC (bridge to ORION) */}
      <section className="border-y border-border/60 bg-slate-deep">
        <div className="container mx-auto max-w-6xl px-4 py-20 md:py-24">
          <div className="grid gap-12 lg:grid-cols-[1fr_auto] lg:items-start lg:gap-16">
            <div>
              <div className="font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-gold">
                The XAU Pro Edge
              </div>
              <h2 className="mt-2 font-display text-3xl font-bold tracking-tight md:text-4xl">
                How We Apply SMC: The ORION Engine
              </h2>
              <p className="mt-5 text-base leading-relaxed text-muted-foreground md:text-lg">
                SMC structure is necessary, but on XAUUSD it is not sufficient. Gold
                is one of the most macro-sensitive instruments in the world. A
                technically perfect setup can be invalidated in seconds by a CPI
                print or an FOMC speech.
              </p>
              <p className="mt-4 text-base leading-relaxed text-muted-foreground md:text-lg">
                That is why we built <span className="font-semibold text-foreground">ORION</span>.
                It applies institutional SMC on the inside, then runs every
                qualifying setup through a deterministic Macro Defense Layer that
                vetoes signals when fundamental conditions turn unsafe. Fewer
                signals. Cleaner execution. Higher trust.
              </p>
              <div className="mt-7">
                <Button asChild variant="gold" size="lg">
                  <Link to="/orion">
                    See How ORION Works <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>
            <div className="flex justify-center lg:justify-end">
              <div className="relative">
                <div className="absolute -inset-3 rounded-2xl bg-gradient-gold opacity-20 blur-xl" aria-hidden />
                <div className="relative flex h-24 w-24 items-center justify-center rounded-2xl border border-gold/40 bg-gold-muted text-gold">
                  <ShieldCheck className="h-12 w-12" />
                </div>
              </div>
            </div>
          </div>

          <div className="mt-12 flex flex-col items-stretch gap-4 lg:flex-row lg:items-center lg:gap-2">
            <PipelineStep
              step={1}
              title="Liquidity Sweep"
              icon={<Droplets className="h-5 w-5" />}
              body="Identifies institutional liquidity grabs."
            />
            <PipelineConnector />
            <PipelineStep
              step={2}
              title="Displacement & BOS"
              icon={<Activity className="h-5 w-5" />}
              body="Confirms structural shift with body close logic."
            />
            <PipelineConnector />
            <PipelineStep
              step={3}
              title="Macro Defense Layer"
              icon={<ShieldCheck className="h-5 w-5" />}
              body="Evaluates real time economic data and volatility regimes."
            />
            <PipelineConnector />
            <PipelineStep
              step={4}
              title="Execution Approval"
              icon={<CheckCircle2 className="h-5 w-5" />}
              body="ORION scores the setup. Signal is generated."
              approved
            />
          </div>
        </div>
      </section>

      {/* 6. Education hub */}
      <section className="container mx-auto max-w-6xl px-4 py-20 md:py-24">
        <div className="mx-auto max-w-2xl text-center">
          <div className="inline-flex items-center gap-2 font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-gold">
            <BookOpen className="h-3 w-3" /> Deep Dives
          </div>
          <h2 className="mt-2 font-display text-3xl font-bold tracking-tight md:text-4xl">
            Master Each Concept
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground md:text-lg">
            Every pillar gets its own institutional-grade lesson. Start with the
            foundation, then layer in liquidity, order blocks, and session timing.
          </p>
        </div>

        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {smcArticles.map((a) => (
            <ArticleCard key={a.slug} article={a} />
          ))}
        </div>

        <div className="mt-10 text-center">
          <Button asChild variant="outline" size="lg">
            <Link to="/education">Browse the Full Education Library</Link>
          </Button>
        </div>
      </section>

      {/* 7. FAQ */}
      <section className="border-y border-border/60 bg-slate-deep">
        <div className="container mx-auto max-w-3xl px-4 py-20 md:py-24">
          <div className="text-center">
            <div className="font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-gold">
              FAQ
            </div>
            <h2 className="mt-2 font-display text-3xl font-bold tracking-tight md:text-4xl">
              Smart Money Concepts: Common Questions
            </h2>
          </div>

          <div className="mt-10 space-y-3">
            {FAQ.map((f) => (
              <details
                key={f.q}
                className="group rounded-xl border border-border/60 bg-card p-5 transition open:border-gold/40"
              >
                <summary className="flex cursor-pointer items-center justify-between gap-4 font-display text-base font-semibold leading-snug">
                  <span>{f.q}</span>
                  <ChevronDown className="h-4 w-4 shrink-0 text-gold transition group-open:rotate-180" />
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* 8. Final CTA */}
      <section className="container mx-auto max-w-3xl px-4 py-20 text-center md:py-28">
        <h2 className="font-display text-3xl font-bold leading-tight tracking-tight md:text-5xl">
          Trade Gold Like the{" "}
          <span className="text-gradient-gold">Institutions Do</span>.
        </h2>
        <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">
          SMC is the framework. ORION is the engine that filters it. Together they
          deliver the institutional discipline that retail trading systems cannot.
        </p>
        <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Button asChild variant="gold" size="xl">
            <Link to="/pricing">
              Join the XAU Terminal™ <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
          <Button asChild variant="outline" size="xl">
            <Link to="/signals">
              <TargetIcon className="h-4 w-4" /> See the Signals Desk
            </Link>
          </Button>
        </div>
        <p className="mt-6 text-[11px] uppercase tracking-wider text-muted-foreground">
          For educational purposes only. Not financial advice.
        </p>
      </section>
    </Layout>
  );
}

function MacroLink({
  href,
  label,
  title,
  body,
}: {
  href: "/gold-cot-report" | "/gold-inflation-cpi" | "/gold-seasonality";
  label: string;
  title: string;
  body: string;
}) {
  return (
    <Link
      to={href}
      className="group flex flex-col rounded-xl border border-border/60 bg-card/60 p-5 transition hover:border-gold/40"
    >
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold">{label}</div>
      <div className="mt-1 font-display text-lg font-semibold">{title}</div>
      <p className="mt-1 flex-1 text-sm text-muted-foreground">{body}</p>
      <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-gold">
        Open the tool
        <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
      </span>
    </Link>
  );
}

function PillarCard({
  num,
  icon,
  title,
  body,
  href,
}: {
  num: number;
  icon: React.ReactNode;
  title: string;
  body: string;
  href: string;
}) {
  return (
    <Link
      to={href}
      className="group flex h-full flex-col rounded-xl border border-border/60 bg-card p-5 transition hover:border-gold/50"
    >
      <div className="flex items-center justify-between">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gold-muted text-gold">
          {icon}
        </div>
        <div className="font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-gold">
          0{num}
        </div>
      </div>
      <h3 className="mt-4 font-display text-base font-semibold leading-snug">{title}</h3>
      <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">{body}</p>
      <div className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-gold transition group-hover:gap-2">
        Read deep dive <ChevronRight className="h-3 w-3" />
      </div>
    </Link>
  );
}

function ContrastItem({ children, positive }: { children: React.ReactNode; positive: boolean }) {
  return (
    <li className="flex items-start gap-2">
      {positive ? (
        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
      ) : (
        <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
      )}
      <span>{children}</span>
    </li>
  );
}

function PipelineStep({
  step,
  title,
  icon,
  body,
  approved,
}: {
  step: number;
  title: string;
  icon: React.ReactNode;
  body: string;
  approved?: boolean;
}) {
  const tileClass = approved
    ? "border border-gold/50 bg-gradient-gold text-gold-foreground shadow-lg shadow-gold/20"
    : "border border-gold/20 bg-gold-muted text-gold";
  return (
    <div
      className={`relative flex-1 rounded-xl border bg-card p-5 transition hover:border-gold/40 ${
        approved ? "border-gold/40" : "border-border/60"
      }`}
    >
      <div className="font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-gold">
        Step {String(step).padStart(2, "0")}
      </div>
      <div className={`mt-3 flex h-10 w-10 items-center justify-center rounded-lg ${tileClass}`}>
        {icon}
      </div>
      <h3 className="mt-4 font-display text-base font-semibold leading-snug">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{body}</p>
    </div>
  );
}

function PipelineConnector() {
  return (
    <div className="flex shrink-0 items-center justify-center text-gold/60 lg:w-8" aria-hidden>
      <div className="flex flex-col items-center lg:hidden">
        <div className="h-4 w-px bg-gradient-to-b from-gold/40 via-gold/20 to-transparent" />
        <ChevronDown className="h-4 w-4" />
      </div>
      <div className="hidden w-full items-center lg:flex">
        <div className="h-px flex-1 bg-gradient-to-r from-gold/10 via-gold/30 to-gold/10" />
        <ChevronRight className="h-4 w-4" />
      </div>
    </div>
  );
}
